import React, { useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Typography,
  Button,
  Box,
} from "@mui/material";
import Color from "../../components/Color";
import { useSnackbar } from "../../App";
import { useMediaQuery, useTheme } from "@mui/material";

const AddBrand = ({ open, onClose, formData, setFormData, onSubmit }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { showSnackbar } = useSnackbar();
  const [errors, setErrors] = useState({});
  const nameInputRef = useRef(null); 

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = ["Brand name is required"];
    } else if (!/^[a-zA-Z ]+$/.test(formData.name)) {
      newErrors.name = ["Brand name must contain only letters"];
    }

    if (!formData.description.trim()) {
      newErrors.description = ["Description is required"];
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      showSnackbar("Please fix validation errors before submitting.", "error");
      return;
    }

    try {
      await onSubmit(setErrors); 
      
    } catch (err) {
      if (err.response?.data?.errors?.name) {
        nameInputRef.current?.focus(); 
        setErrors((prev) => ({
          ...prev,
          name: err.response.data.errors.name,
        }));
      }
    }
  };

  const handleCancel = () => {
    setFormData({
      id: null,
      name: "",
      description: "",
    });
    setErrors({});
    onClose();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "name") {
      const isValid = /^[a-zA-Z ]*$/.test(value);
      const filtered = value.replace(/[^a-zA-Z ]/g, "");
      setFormData((prev) => ({
        ...prev,
        [name]: filtered,
      }));
      if (!isValid) {
        setErrors((prev) => ({
          ...prev,
          name: ["Only letters are allowed"],
        }));
      } else {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.name;
          return newErrors;
        });
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(e, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          if (Object.keys(errors).length > 0) {
            showSnackbar("Please fix validation errors before closing.", "error");
            return; 
          }
          handleCancel();
        } else {
          handleCancel();
        }
      }}
      disableEscapeKeyDown={Object.keys(errors).length > 0}
      PaperProps={{
        sx: {
          width: isMobile ? "90vw" : "400px",
          maxWidth: "95vw",
          borderRadius: "8px",
        },
      }}
    >
      <DialogTitle sx={{ fontSize: isMobile ? "1rem" : "1.25rem" }}>
        {formData.id ? "Edit Brand" : "Add Brand"}
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          px: isMobile ? 2 : 3,
          py: isMobile ? 1 : 2,
          maxHeight: isMobile ? "60vh" : "auto",
          overflowY: "auto",
        }}
      >
        <form onSubmit={handleSubmit}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: 1,
            }}
          >
            {/* Brand Name */}
            <Box mb={1}>
              <Typography
                variant="body1"
                sx={{
                  mb: 1,
                  fontWeight: "bold",
                  color: Color.label.main,
                  fontSize: isMobile ? "12px" : "13px",
                }}
              >
                Brand Name*
              </Typography>
              <TextField
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter brand name"
                fullWidth
                error={!!errors.name}
                helperText={errors.name?.[0]}
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                inputRef={nameInputRef}
                sx={{
                  "& .MuiInputLabel-root": { display: "none" },
                  "& .MuiInputBase-input": { fontSize: isMobile ? "0.85rem" : "1rem" },
                  "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
                    borderColor: "error.main",
                  },
                }}
              />
            </Box>

            {/* Description */}
            <Box mb={1.5}>
              <Typography
                variant="body1"
                sx={{
                  mb: 1,
                  fontWeight: "bold",
                  color: Color.label.main,
                  fontSize: isMobile ? "12px" : "13px",
                }}
              >
                Description*
              </Typography>
              <TextField
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Enter description"
                fullWidth
                error={!!errors.description}
                helperText={errors.description?.[0]}
                multiline
                rows={4}
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                sx={{
                  "& .MuiInputLabel-root": { display: "none" },
                  "& .MuiInputBase-inputMultiline": {
                    paddingTop: "8px",
                    paddingBottom: "8px",
                    lineHeight: 1.5,
                    fontSize: isMobile ? "0.85rem" : "1rem",
                  },
                }}
              />
            </Box>
          </Box>

          
          {errors.form && (
            <Typography color="error" variant="body2" mt={1}>
              {errors.form}
            </Typography>
          )}

          
          <Box display="flex" justifyContent="flex-end" gap={2} mt={1} mb={1}>
            <Button
              onClick={handleCancel}
              sx={{
                background: "none",
                color: Color.secondary.main,
                border: "1px solid",
                borderColor: Color.secondary.main,
                width: isMobile ? "100%" : "auto",
                fontSize: isMobile ? "0.85rem" : "1rem",
                padding: isMobile ? "8px" : "6px 16px",
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              sx={{
                background: Color.primary.main,
                color: "white",
                width: isMobile ? "100%" : "auto",
                fontSize: isMobile ? "0.85rem" : "1rem",
                padding: isMobile ? "8px" : "6px 16px",
                "&:hover": {
                  background: Color.primary.dark,
                },
              }}
            >
              {formData.id ? "Update" : "Submit"}
            </Button>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddBrand;
